import React from "react";
import { Link } from "react-router-dom";
import {
  CalendarCheck, Award, Clock3, BadgeCheck, ArrowRight, GraduationCap,
  Smartphone, Calculator, MapPin, Phone, ShieldCheck, Users, FileCheck2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { SITE, IMAGES, TESTS } from "../mock";

const stats = [
  { icon: ShieldCheck, value: "Professional, Reliable, Secure", label: "Testing" },
  { icon: Clock3, value: "2 Days", label: "Results Turnaround" },
  { icon: BadgeCheck, value: "Official", label: "ETS Authorized Center" },
  { icon: CalendarCheck, value: "Mon–Fri", label: "10:00 AM & 11:30 AM" },
];

const steps = [
  { n: "01", title: "Choose Your Test", desc: "Select the TOEIC test required for your work permit or professional goals." },
  { n: "02", title: "Pick a Date & Time", desc: "Tests run Monday to Friday at 10:00 AM and 11:30 AM. Choose the slot that suits you." },
  { n: "03", title: "Register Online", desc: "Fill in the employer and testee information and receive instant confirmation." },
  { n: "04", title: "Take the Test", desc: "Arrive 20 minutes early with 2 forms of ID (including passport). Results in 2 business days." },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#F2FAFB] to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-16 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <Badge className="bg-[#E8F7F9] text-[#0C8695] hover:bg-[#E8F7F9] font-semibold px-4 py-1.5 rounded-full mb-6 border border-[#B5E6EC]">
              Official TOEIC® Testing Center • Cayman Islands
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-[3.4rem] font-extrabold text-[#0A2540] leading-[1.08] tracking-tight">
              English Language Testing for Your{" "}
              <span className="text-[#0E9AAA]">Work Permit</span> & Career
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl">
              Modern Business Solutions is the Cayman Islands' authorized TOEIC® testing
              center. Book your Redesigned TOEIC Bridge Listening & Reading Test online in minutes —
              Monday to Friday at 10:00 AM and 11:30 AM.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link to="/book?test=bridge-lr">
                <Button
                  data-testid="hero-book-btn"
                  size="lg"
                  className="bg-[#0E9AAA] hover:bg-[#0C8695] text-white font-bold rounded-lg px-8 h-13 text-base shadow-lg hover:shadow-xl transition-shadow h-12"
                >
                  Book Your Test <ArrowRight size={18} className="ml-2" />
                </Button>
              </Link>
            </div>
            <div className="mt-8 flex items-center gap-6 text-sm text-slate-500">
              <span className="flex items-center gap-2"><ShieldCheck size={16} className="text-[#0E9AAA]" /> WORC Accepted</span>
              <span className="flex items-center gap-2"><Clock3 size={16} className="text-[#0E9AAA]" /> Results in 2 business days</span>
            </div>
          </div>
          <div className="relative">
            <div className="absolute -top-6 -right-6 w-40 h-40 bg-[#0E9AAA]/10 rounded-full blur-2xl" />
            <img
              src={IMAGES.examRoom}
              alt="Test taker at MBS testing center"
              className="rounded-2xl shadow-2xl w-full h-[420px] object-cover"
            />
            <Card className="absolute -bottom-8 left-4 sm:left-8 shadow-xl border-0 rounded-xl">
              <CardContent className="p-5 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#E8F7F9] flex items-center justify-center">
                  <CalendarCheck className="text-[#0E9AAA]" size={24} />
                </div>
                <div>
                  <div className="font-extrabold text-[#0A2540]">Next Available Tests</div>
                  <div className="text-sm text-slate-500">Mon–Fri • 10:00 AM & 11:30 AM</div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-[#0A2540] py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((s) => (
            <div key={s.label} className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
                <s.icon className="text-[#39C1D0]" size={22} />
              </div>
              <div>
                <div className="text-white font-extrabold text-xl leading-none">{s.value}</div>
                <div className="text-slate-400 text-xs mt-1.5">{s.label}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* TESTS */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="text-[#0E9AAA] font-bold text-sm uppercase tracking-widest mb-3">Our Tests</div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0A2540] tracking-tight">
              TOEIC® Tests We Administer
            </h2>
            <p className="mt-4 text-slate-600">
              All tests are administered at our George Town test center by certified proctors.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-7">
            {TESTS.map((t) => (
              <Card
                key={t.id}
                data-testid={`test-card-${t.id}`}
                className="rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col"
              >
                <CardContent className="p-7 flex flex-col flex-1">
                  <Badge className="self-start bg-[#E8F7F9] text-[#0C8695] hover:bg-[#E8F7F9] border border-[#B5E6EC] font-semibold mb-5">
                    {t.badge}
                  </Badge>
                  <h3 className="font-extrabold text-lg text-[#0A2540] leading-snug">{t.shortName}</h3>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed flex-1">{t.description}</p>
                  <div className="mt-5 space-y-2 text-sm text-slate-500">
                    <div className="flex items-center gap-2"><Clock3 size={15} className="text-[#0E9AAA]" /> {t.schedule}</div>
                    <div className="flex items-center gap-2"><Award size={15} className="text-[#0E9AAA]" /> {t.scoreScale}</div>
                  </div>
                  <div className="mt-6 pt-5 border-t border-slate-100 flex items-center justify-between">
                    <span className="font-extrabold text-[#0A2540] text-xl">CI ${t.price}</span>
                    <Link to={`/book?test=${t.id}`}>
                      <Button size="sm" className="bg-[#0A2540] hover:bg-[#123B63] text-white font-bold rounded-lg transition-colors">
                        Book Now
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 bg-[#F6FAFB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="text-[#0E9AAA] font-bold text-sm uppercase tracking-widest mb-3">Simple Process</div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0A2540] tracking-tight">
              Scheduling Your English Language Test is Easy
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-7">
            {steps.map((s) => (
              <div key={s.n} className="bg-white rounded-2xl p-7 border border-slate-100 shadow-sm hover:shadow-lg transition-shadow duration-300">
                <div className="text-[#0E9AAA] font-extrabold text-3xl mb-4 opacity-40">{s.n}</div>
                <h3 className="font-bold text-[#0A2540] text-lg">{s.title}</h3>
                <p className="mt-2.5 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/book?test=bridge-lr">
              <Button size="lg" className="bg-[#0E9AAA] hover:bg-[#0C8695] text-white font-bold rounded-lg px-10 h-12 shadow-lg hover:shadow-xl transition-shadow">
                Start Registration <ArrowRight size={18} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* WORC CALLOUT */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="bg-[#0A2540] rounded-3xl p-8 sm:p-12 grid lg:grid-cols-2 gap-10 items-center overflow-hidden relative">
            <div className="absolute top-0 right-0 w-72 h-72 bg-[#0E9AAA]/10 rounded-full blur-3xl" />
            <div className="relative">
              <div className="text-[#39C1D0] font-bold text-sm uppercase tracking-widest mb-3">Work Permit Applicants</div>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight leading-snug">
                Need an English Test for Your Cayman Islands Work Permit?
              </h2>
              <p className="mt-4 text-slate-300 leading-relaxed">
                The Workforce Opportunities and Residency Cayman (WORC) department requires a
                minimum score of <strong className="text-white">Level A2 in Listening</strong> and{" "}
                <strong className="text-white">Level A1 in Reading</strong> on the Redesigned TOEIC Bridge test.
                We'll help you understand exactly what your scores mean.
              </p>
              <div className="mt-7 flex flex-wrap gap-4">
                <Link to="/book?test=bridge-lr">
                  <Button className="bg-[#0E9AAA] hover:bg-[#0C8695] text-white font-bold rounded-lg px-7 transition-colors">
                    Book Bridge Test
                  </Button>
                </Link>
                <Link to="/scores">
                  <Button variant="outline" className="border-white/40 bg-transparent text-white hover:bg-white hover:text-[#0A2540] font-bold rounded-lg px-7 transition-colors">
                    Understand Scores
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <img src={IMAGES.workers} alt="Professional workers" className="rounded-2xl h-72 w-full object-cover shadow-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* OTHER SERVICES */}
      <section className="py-20 bg-[#F6FAFB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="text-[#0E9AAA] font-bold text-sm uppercase tracking-widest mb-3">Beyond Testing</div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0A2540] tracking-tight">More Ways We Help Your Business</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-7">
            <Card className="rounded-2xl border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden group">
              <img src={IMAGES.examWriting} alt="TOEIC testing" className="h-44 w-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <CardContent className="p-7">
                <div className="flex items-center gap-3 mb-3">
                  <GraduationCap className="text-[#0E9AAA]" size={22} />
                  <h3 className="font-extrabold text-[#0A2540] text-lg">TOEIC® Testing</h3>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">Official English proficiency testing for work permits, education and professional certification.</p>
                <Link to="/services" className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-[#0E9AAA] hover:gap-3 transition-all">
                  Learn more <ArrowRight size={15} />
                </Link>
              </CardContent>
            </Card>
            <Card className="rounded-2xl border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden group">
              <img src={IMAGES.appDev} alt="App development" className="h-44 w-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <CardContent className="p-7">
                <div className="flex items-center gap-3 mb-3">
                  <Smartphone className="text-[#0E9AAA]" size={22} />
                  <h3 className="font-extrabold text-[#0A2540] text-lg">App Development</h3>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">Custom web and mobile applications built for Cayman businesses — from idea to launch.</p>
                <Link to="/app-development" className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-[#0E9AAA] hover:gap-3 transition-all">
                  Learn more <ArrowRight size={15} />
                </Link>
              </CardContent>
            </Card>
            <Card className="rounded-2xl border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden group">
              <img src={IMAGES.accounting} alt="Blue Ledger accounting" className="h-44 w-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <CardContent className="p-7">
                <div className="flex items-center gap-3 mb-3">
                  <Calculator className="text-[#0E9AAA]" size={22} />
                  <h3 className="font-extrabold text-[#0A2540] text-lg">Blue Ledger</h3>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">Our modern accounting platform with first-class Cayman Islands support and no-tax mode.</p>
                <Link to="/blue-ledger" className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-[#0E9AAA] hover:gap-3 transition-all">
                  Learn more <ArrowRight size={15} />
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CONTACT STRIP */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid md:grid-cols-3 gap-7">
          <div className="flex items-start gap-4 p-6 rounded-2xl border border-slate-100 bg-[#F6FAFB]">
            <MapPin className="text-[#0E9AAA] mt-1 shrink-0" size={22} />
            <div>
              <div className="font-bold text-[#0A2540]">Our Location</div>
              <p className="text-sm text-slate-600 mt-1">{SITE.address}<br />{SITE.addressNote}</p>
            </div>
          </div>
          <div className="flex items-start gap-4 p-6 rounded-2xl border border-slate-100 bg-[#F6FAFB]">
            <Phone className="text-[#0E9AAA] mt-1 shrink-0" size={22} />
            <div>
              <div className="font-bold text-[#0A2540]">Call or Email</div>
              <p className="text-sm text-slate-600 mt-1">{SITE.phone}<br />{SITE.email}</p>
            </div>
          </div>
          <div className="flex items-start gap-4 p-6 rounded-2xl border border-slate-100 bg-[#F6FAFB]">
            <FileCheck2 className="text-[#0E9AAA] mt-1 shrink-0" size={22} />
            <div>
              <div className="font-bold text-[#0A2540]">Sister Islands</div>
              <p className="text-sm text-slate-600 mt-1">To schedule a test in the Sister Islands please email {SITE.sisterIslandsEmail}</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
